/**
 * Design Phase Dashboard
 * Enterprise Architecture Design with Well-Architected Framework
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  Shield,
  Lock,
  DollarSign,
  Settings,
  Zap,
  Layers,
  Globe,
  Database,
  Network,
  BookOpen,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  Clock,
  Users,
  Target,
  Workflow,
  GitBranch,
  Presentation,
  FileText,
  Activity
} from "lucide-react";
import { StressTestingPresentations } from "@/components/StressTestingPresentations";

import { designFrameworkData } from '../../../shared/design-framework';

export default function DesignPage() {
  const [selectedPattern, setSelectedPattern] = useState<string | null>(null);
  const [selectedDecision, setSelectedDecision] = useState<string | null>(null);

  const { wellArchitectedPillars, layeredArchitectureLayers, distributedPatterns, sampleDesignDecisions } = designFrameworkData;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in-progress': return 'bg-shiftleft-100 text-shiftleft-800';
      case 'proposed': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'implemented': return 'bg-green-100 text-green-800';
      case 'deprecated': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'very-high': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const wellArchitectedIcons = {
    reliability: Shield,
    security: Lock,
    'cost-optimization': DollarSign,
    'operational-excellence': Settings,
    'performance-efficiency': Zap
  };

  const layerIcons = {
    presentation: Globe,
    application: Workflow,
    domain: Target,
    infrastructure: Database
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Enterprise Architecture Design</h1>
        <p className="text-gray-600 mt-1">Well-Architected Framework, Layered Architecture, and Distributed Design Patterns</p>
      </div>

      {/* Architecture Framework Tabs */}
      <Tabs defaultValue="well-architected" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="well-architected">Well-Architected Framework</TabsTrigger>
          <TabsTrigger value="layered">Layered Architecture</TabsTrigger>
          <TabsTrigger value="distributed">Distributed Patterns</TabsTrigger>
          <TabsTrigger value="presentations">Presentations</TabsTrigger>
          <TabsTrigger value="decisions">Architecture Decisions</TabsTrigger>
        </TabsList>

        {/* Well-Architected Framework */}
        <TabsContent value="well-architected" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {wellArchitectedPillars.map((pillar) => {
              const IconComponent = wellArchitectedIcons[pillar.id as keyof typeof wellArchitectedIcons];
              return (
                <Card key={pillar.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <IconComponent className="w-5 h-5 text-shiftleft-600" />
                      {pillar.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-gray-600">{pillar.description}</p>

                    <div>
                      <h4 className="text-sm font-semibold mb-2">Key Principles</h4>
                      <div className="space-y-1">
                        {pillar.principles.slice(0, 3).map((principle, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="w-3 h-3 text-green-600" />
                            {principle}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold mb-2">Design Patterns</h4>
                      <div className="flex flex-wrap gap-1">
                        {pillar.designPatterns.slice(0, 4).map((pattern, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {pattern}
                          </Badge>
                        ))}
                        {pillar.designPatterns.length > 4 && (
                          <Badge variant="outline" className="text-xs">
                            +{pillar.designPatterns.length - 4} more
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold mb-2">Azure Services</h4>
                      <div className="flex flex-wrap gap-1">
                        {pillar.azureServices.slice(0, 3).map((service, index) => (
                          <Badge key={index} variant="outline" className="text-xs bg-shiftleft-50 text-shiftleft-700">
                            {service}
                          </Badge>
                        ))}
                        {pillar.azureServices.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{pillar.azureServices.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>

                    <Button variant="outline" size="sm" className="w-full">
                      View Implementation Guide
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        {/* Layered Architecture */}
        <TabsContent value="layered" className="space-y-6">
          <div className="space-y-4">
            <div className="bg-shiftleft-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">Layered Architecture Overview</h3>
              <p className="text-gray-600">A structured approach to organizing code into logical layers with clear responsibilities and dependencies</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {layeredArchitectureLayers.map((layer, index) => {
                const IconComponent = layerIcons[layer.id as keyof typeof layerIcons];
                return (
                  <Card key={layer.id} className="hover:shadow-md transition-shadow">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <IconComponent className="w-5 h-5 text-green-600" />
                        {layer.name}
                        <Badge variant="outline" className="ml-auto">
                          Layer {index + 1}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-gray-600">{layer.description}</p>

                      <div>
                        <h4 className="text-sm font-semibold mb-2">Responsibilities</h4>
                        <ul className="space-y-1">
                          {layer.responsibilities.slice(0, 3).map((responsibility, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm">
                              <Target className="w-3 h-3 text-green-600" />
                              {responsibility}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold mb-2">Technologies</h4>
                        <div className="flex flex-wrap gap-1">
                          {layer.technologies.slice(0, 4).map((tech, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {tech}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold mb-2">Azure Services</h4>
                        <div className="flex flex-wrap gap-1">
                          {layer.azureServices.slice(0, 3).map((service, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs bg-shiftleft-50 text-shiftleft-700">
                              {service}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold mb-2">Dependencies</h4>
                        <div className="flex flex-wrap gap-1">
                          {layer.dependencies.length > 0 ? (
                            layer.dependencies.map((dep, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs text-orange-700">
                                {dep} layer
                              </Badge>
                            ))
                          ) : (
                            <Badge variant="outline" className="text-xs text-green-700">
                              No dependencies
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Distributed Patterns */}
        <TabsContent value="distributed" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {distributedPatterns.map((pattern) => (
              <Card
                key={pattern.id}
                className={`hover:shadow-md transition-shadow cursor-pointer ${selectedPattern === pattern.id ? 'ring-2 ring-shiftleft-500' : ''
                  }`}
                onClick={() => setSelectedPattern(pattern.id)}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Network className="w-4 h-4 text-purple-600" />
                    {pattern.name}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Badge variant="outline" className="text-xs capitalize">
                      {pattern.type}
                    </Badge>
                    <Badge className={`text-xs ${getComplexityColor(pattern.complexity)}`}>
                      {pattern.complexity} complexity
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">{pattern.description}</p>

                  <div>
                    <h4 className="text-sm font-semibold mb-2">Implementation</h4>
                    <p className="text-xs text-gray-600">{pattern.implementation}</p>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold mb-2">Azure Services</h4>
                    <div className="flex flex-wrap gap-1">
                      {pattern.azureServices.map((service, index) => (
                        <Badge key={index} variant="outline" className="text-xs bg-shiftleft-50 text-shiftleft-700">
                          {service}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold mb-2">Prerequisites</h4>
                    <ul className="space-y-1">
                      {pattern.prerequisites.slice(0, 2).map((prereq, index) => (
                        <li key={index} className="flex items-center gap-2 text-xs">
                          <CheckCircle className="w-3 h-3 text-green-600" />
                          {prereq}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {selectedPattern && (
            <Card className="bg-shiftleft-50 border-shiftleft-200">
              <CardHeader>
                <CardTitle className="text-lg">Pattern Deep Dive</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">
                  Selected pattern: <strong>{distributedPatterns.find(p => p.id === selectedPattern)?.name}</strong>
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Click on a different pattern to explore its implementation details, or use the sidebar to access the pattern library.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Presentations */}
        <TabsContent value="presentations" className="space-y-6">
          <div className="bg-shiftleft-50 p-6 rounded-lg mb-6">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <Presentation className="w-5 h-5 text-shiftleft-600" />
              Stakeholder Presentations
            </h3>
            <p className="text-gray-600">
              Audience-specific presentations for Costco stakeholders addressing JMeter limitations and database stress testing benefits
            </p>
          </div>
          <StressTestingPresentations />
        </TabsContent>

        {/* Architecture Decisions */}
        <TabsContent value="decisions" className="space-y-6">
          <div className="space-y-4">
            <div className="bg-orange-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">Architecture Decision Records (ADRs)</h3>
              <p className="text-gray-600">Documenting important architectural decisions for the HighJump WMS transformation</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {sampleDesignDecisions.map((decision) => (
                <Card
                  key={decision.id}
                  className={`hover:shadow-md transition-shadow cursor-pointer ${selectedDecision === decision.id ? 'ring-2 ring-orange-500' : ''
                    }`}
                  onClick={() => setSelectedDecision(decision.id)}
                >
                  <CardHeader>
                    <CardTitle className="flex items-between gap-2">
                      <BookOpen className="w-4 h-4 text-orange-600" />
                      {decision.title}
                    </CardTitle>
                    <div className="flex gap-2">
                      <Badge className={`text-xs ${getStatusColor(decision.status)}`}>
                        {decision.status}
                      </Badge>
                      {decision.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold mb-1">Context</h4>
                      <p className="text-xs text-gray-600">{decision.context}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold mb-1">Problem</h4>
                      <p className="text-xs text-gray-600">{decision.problem}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold mb-2">Options ({decision.options.length})</h4>
                      <div className="space-y-2">
                        {decision.options.map((option, index) => (
                          <div key={index} className="p-2 bg-gray-50 rounded text-xs">
                            <div className="font-medium">{option.name}</div>
                            <div className="flex gap-2 mt-1">
                              <Badge className={`text-xs ${getComplexityColor(option.complexity)}`}>
                                {option.complexity} complexity
                              </Badge>
                              <Badge className={`text-xs ${getComplexityColor(option.cost)}`}>
                                {option.cost} cost
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold mb-1">Recommendation</h4>
                      <p className="text-xs text-green-700 font-medium">{decision.recommendation}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {selectedDecision && (
              <Card className="bg-orange-50 border-orange-200">
                <CardHeader>
                  <CardTitle className="text-lg">Decision Details</CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const decision = sampleDesignDecisions.find(d => d.id === selectedDecision);
                    return decision ? (
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold mb-2">Rationale</h4>
                          <p className="text-sm text-gray-600">{decision.rationale}</p>
                        </div>

                        <div>
                          <h4 className="text-sm font-semibold mb-2">Consequences</h4>
                          <ul className="space-y-1">
                            {decision.consequences.map((consequence, index) => (
                              <li key={index} className="flex items-center gap-2 text-sm">
                                <AlertTriangle className="w-3 h-3 text-orange-600" />
                                {consequence}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ) : null;
                  })()}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
